import React, { useState } from 'react';
import { List, useTable, ShowButton } from '@refinedev/antd';
import { Table, Tag, Space, Select, Statistic, Card, Row, Col } from 'antd';
import { useCustom } from '@refinedev/core';

const STATUS_COLORS: Record<string, string> = {
  pending: '#fa8c16',
  processing: '#1890ff',
  completed: '#52c41a',
  failed: '#ff4d4f',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const VisionQueueJobList: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('failed');

  const { data: metricsData, isLoading: metricsLoading } = useCustom({
    url: '/vision-admin/queue/metrics',
    method: 'get',
  });

  const metrics = metricsData?.data as {
    counts: Record<string, number>;
    totals: { processedToday: number; failedToday: number };
    health: { oldestWaitingJobAgeMs: number | null; failureRatePercent: number | null };
  } | undefined;

  const { tableProps } = useTable({
    resource: 'vision-queue-jobs',
    filters: {
      permanent: [{ field: 'status', operator: 'eq' as const, value: statusFilter }],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="vision-queue-jobs" canCreate={false} title="Vision Queue Jobs">
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Waiting"
              value={metrics?.counts?.waiting ?? 0}
              valueStyle={{ color: '#fa8c16' }}
              loading={metricsLoading}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Active"
              value={metrics?.counts?.active ?? 0}
              valueStyle={{ color: '#1890ff' }}
              loading={metricsLoading}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Completed"
              value={metrics?.counts?.completed ?? 0}
              valueStyle={{ color: '#52c41a' }}
              loading={metricsLoading}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Failed"
              value={metrics?.counts?.failed ?? 0}
              valueStyle={{ color: '#ff4d4f' }}
              loading={metricsLoading}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Processed Today"
              value={metrics?.totals?.processedToday ?? 0}
              loading={metricsLoading}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Failure Rate"
              value={metrics?.health?.failureRatePercent ?? 0}
              suffix="%"
              precision={1}
              valueStyle={{ color: metrics?.health?.failureRatePercent && metrics.health.failureRatePercent > 10 ? '#ff4d4f' : '#52c41a' }}
              loading={metricsLoading}
            />
          </Card>
        </Col>
      </Row>

      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <Select
          placeholder="Filter by status"
          style={{ width: 180 }}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
          options={[
            { label: 'Waiting', value: 'waiting' },
            { label: 'Active', value: 'active' },
            { label: 'Completed', value: 'completed' },
            { label: 'Failed', value: 'failed' },
            { label: 'Delayed', value: 'delayed' },
          ]}
        />
      </div>

      <Table {...tableProps} rowKey="id" scroll={{ x: 1000 }}>
        <Table.Column dataIndex="id" title="Job ID" width={120} render={(id: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{id}</span>} />
        <Table.Column dataIndex="type" title="Type" width={160} />
        <Table.Column
          dataIndex="status"
          title="Status"
          width={120}
          render={(status: string) => (
            <Tag style={tagStyle(STATUS_COLORS[status] || '#898989')}>{status}</Tag>
          )}
        />
        <Table.Column dataIndex="attemptsMade" title="Attempts" width={100} />
        <Table.Column
          dataIndex="createdAt"
          title="Created"
          width={180}
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          dataIndex="finishedAt"
          title="Finished"
          width={180}
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          dataIndex="failedReason"
          title="Error / Result"
          render={(_: any, record: any) => {
            if (record.failedReason) {
              return <span style={{ color: '#ff4d4f' }}>{record.failedReason.length > 60 ? `${record.failedReason.slice(0, 60)}...` : record.failedReason}</span>;
            }
            if (record.result?.result?.from) {
              return <Tag color="blue">{record.result.result.from}</Tag>;
            }
            if (record.result?.status === 'completed') {
              return <Tag color="green">completed</Tag>;
            }
            return '—';
          }}
        />
        <Table.Column
          title="Actions"
          width={100}
          render={(_: any, record: any) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="vision-queue-jobs" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
